import React, { useEffect, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Hub } from 'aws-amplify/utils';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { postLoginCallback } from './routing/utils/LoginUtils';
import TransitionScreen from './common/components/TransitionScreen';
import userContextSlice from './common/slices/userContextSlice';
import { UserContextInitializationState } from './common/slices/userContextSlice';
import { refreshAuthToken } from './common/utils/authUtils';
import { clearCognitoStorage } from './common/utils/authFetch';
import { userContextApi } from './common/api/userContextApi';
import { organizationContextApi } from './common/api/organizationContextApi';
import { AuthenticationError } from './areas/authentication/components/AuthenticationError';
import { GuidedExperienceProvider } from './areas/guided-experience';

const AuthenticatedApp = ({ children }: { children: React.ReactNode }) => {
  const { signOut, authStatus, route } = useAuthenticator();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const initializationState = useSelector(userContextSlice.selectors.initializationState);
  const authError = useSelector((state: any) => state.userContext.error);
  const currentOrgId = useSelector(userContextSlice.selectors.getCurrentOrganization)?.id;
  
  const isInitialized = initializationState === UserContextInitializationState.ORGANIZATION_INITIALIZED;
  
  // Subscribe to user context (account info, org list)
  userContextApi.useApiPortalV1ContextRetrieveQuery(undefined as void, {
    skip: !isInitialized,
  });
  
  // Subscribe to organization context (members, assessment periods, etc.)
  organizationContextApi.useApiPortalV1OrganizationContextRetrieveQuery(
    { organizationId: currentOrgId! },
    {
      skip: !isInitialized || !currentOrgId,
    }
  );

  useEffect(() => {
    const setupUser = async () => {
      try {
        // Wait for Amplify to report a definitive auth status before calling the login callback.
        const authStatusDetermined = authStatus === 'authenticated' || authStatus === 'unauthenticated';
        const routeDetermined = route !== 'transition';

        if (!authStatusDetermined || !routeDetermined) {
          // Wait for auth state to become definitive before rendering the app.
          return;
        }

      if (authStatus !== 'authenticated' || route !== 'authenticated') {
          setIsLoading(false);
          return;
        }

        // Skip auto-login if we're on the logout path
        if (location.pathname === '/logout') {
          setIsLoading(false);
          return;
        }

        // Skip initialization if there's already an auth error
        if (authError) {
          setIsLoading(false);
          return;
        }

        // Only initialize if not already initialized
        if (initializationState === UserContextInitializationState.NOT_INITIALIZED) {
          // Check if there's already a token in localStorage to determine if this is a refresh
          const existingToken = localStorage.getItem('cognito_token');
          const isRefresh = !!existingToken;

          await postLoginCallback(signOut, navigate, dispatch, isRefresh, {
            isMobileDevice: isMobile,
          });
        }
        setIsLoading(false);
      } catch (error: any) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        console.error('Error setting up user session', 'AuthenticatedApp', { error: errorMessage, originalError: error });

        // Check if this is a user-blocked error first
        const errorString = error instanceof Error ? error.message : String(error);
        const isUserBlockedError = 
          errorString.includes('USER_BLOCKED:') ||
          errorString.includes('no longer active') || 
          errorString.includes('do not have access to any organizations');

        if (isUserBlockedError) {
          // Extract the actual message after USER_BLOCKED: prefix
          const actualMessage = errorString.includes('USER_BLOCKED:') 
            ? errorString.replace('USER_BLOCKED: ', '')
            : errorString;
          
          // User is blocked - dispatch to Redux
          dispatch(userContextSlice.actions.setAuthenticationError(actualMessage));
          setIsLoading(false);
          return;
        }

        // Set error message based on the type of error
        const isTokenRevoked =
          errorString.includes('NotAuthorizedException') ||
          errorString.includes('Access Token has been revoked');

        // Attempt token refresh if token was revoked
        if (isTokenRevoked) {
          try {
            const refreshedToken = await refreshAuthToken();
            if (refreshedToken) {
              // Retry the setup with refreshed token
              const existingToken = localStorage.getItem('cognito_token');
              const isRefresh = !!existingToken;
              await postLoginCallback(signOut, navigate, dispatch, isRefresh);
              setIsLoading(false);
              return; // Success, exit early
            }
          } catch (refreshError) {
            console.error('Token refresh failed', 'AuthenticatedApp', { error: refreshError });
            // Fall through to show error
          }
          setError('Your session has expired or been revoked. Please sign in again.');
        } else {
          setError('There was a problem setting up your session. Please try again.');
        }

        setIsLoading(false);
      }
    };

    setupUser();
    // never add navigation or location.pathname to the dependencies array
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, signOut, initializationState, authStatus, route]);

  // Amplify dispatches `tokenRefresh_failure` whenever a background or explicit
  // refresh attempt fails (revoked/expired refresh token, pool-mismatch after rotation,
  // etc.). Without this listener those failures only surface on the next 401 from
  // an RTK request — leaving any non-RTK code path stuck in a logged-in-looking
  // state. Reacting at the source guarantees the user is sent back to login.
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'tokenRefresh_failure') {
        console.warn('Amplify Hub: tokenRefresh_failure; signing out');
        clearCognitoStorage();
        signOut();
      }
    });
    return unsubscribe;
  }, [signOut]);

  if (isLoading) {
    return <TransitionScreen message="Preparing your nuLogic experience" signOut={signOut} />;
  }

  if (authError) {
    return <AuthenticationError message={authError} signOut={signOut} />;
  }

  if (error) {
    return <TransitionScreen message={error} isError={true} signOut={signOut} />;
  }

  return (
    <GuidedExperienceProvider>
      <div data-testid="app-ready">{children}</div>
    </GuidedExperienceProvider>
  );
};

export default AuthenticatedApp;
